
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { SparklesIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import useLocale from 'next-translate/useTranslation';
import Link from 'next/link';
import React, { useState, useRef } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import translateReactTree from '@/utils/translateReactTree';
const features = [
    {
        title: 'Works with Any Portrait',
        description: 'Upload photos, illustrations, AI-generated images, or even painted portraits. Our AI handles them all with stunning results.',
        icon: '🖼️',
    },
    {
        title: 'Realistic Lip-Sync',
        description: 'Advanced AI-powered mouth movement technology ensures every word matches perfectly with natural lip animations.',
        icon: '👄',
    },
    {
        title: 'Natural Head Motion',
        description: 'Subtle head movements and micro-expressions add incredible realism, making your talking photos look truly alive.',
        icon: '🎭',
    },
    {
        title: '40+ Languages',
        description: 'Make your image speak in over 40 languages with native-sounding pronunciation and natural intonation.',
        icon: '🌍',
    },
    {
        title: 'Multiple Voice Options',
        description: 'Choose from dozens of AI voices or upload your own audio recording for perfect voice matching.',
        icon: '🎙️',
    },
    {
        title: 'Auto-Generated Captions',
        description: 'Add TikTok-style animated subtitles automatically. Perfect for social media engagement and accessibility.',
        icon: '📝',
    },
    {
        title: 'Fast Processing',
        description: 'Get your talking photo video in just 2-5 minutes. No lengthy rendering or complicated software required.',
        icon: '⚡',
    },
    {
        title: 'HD Output Quality',
        description: 'Crystal clear 1080p video output ensures your talking photos look professional on any platform.',
        icon: '📺',
    },
    {
        title: 'Commercial Usage',
        description: 'Full commercial rights included. Use your talking photo videos for business, marketing, and monetization.',
        icon: '💼',
    },
];
const imageTypes = [
    {
        icon: '📸',
        title: 'Portrait Photos',
        description: 'Clear face shots, front-facing or slight angle. Selfies, group photo crops, and candid portraits all work great.',
    },
    {
        icon: '👔',
        title: 'Professional Headshots',
        description: 'Business photos, LinkedIn-style portraits, and corporate headshots produce excellent, polished results.',
    },
    {
        icon: '🤖',
        title: 'AI-Generated Images',
        description: 'Images created with AI generators like DALL-E, Midjourney, or our own AI Image Generator work perfectly.',
    },
    {
        icon: '🎨',
        title: 'Illustrations & Artwork',
        description: 'Digital art, painted portraits, anime characters, and cartoon-style illustrations can all be animated.',
    },
    {
        icon: '📜',
        title: 'Historical Photos',
        description: 'Old photographs, vintage portraits, and archival images can be brought back to life with stunning realism.',
    },
];
const steps = [
    {
        number: '01',
        title: 'Upload Your Image',
        description: 'Upload any portrait photo or generate one with our AI Image Generator. JPG, PNG, and WEBP formats are all supported. The image should have a clearly visible face for the best results.',
    },
    {
        number: '02',
        title: 'Write Your Script',
        description: 'Enter the text you want your image to say. You can type out a full script or upload your own audio recording for perfect voice matching. Keep it natural for the most realistic output.',
    },
    {
        number: '03',
        title: 'Choose Voice & Language',
        description: 'Select from dozens of AI voices in 40+ languages. Adjust speed and tone to match your content. Preview voices before generating to find the perfect match for your talking photo.',
    },
    {
        number: '04',
        title: 'Generate & Download',
        description: 'Click generate and your talking photo will be ready in just a few minutes. Download in MP4 format, ready to share on social media, embed on your website, or use in presentations.',
    },
];
const useCases = [
    {
        title: 'Social Media Content',
        description: 'Create scroll-stopping talking photo videos for TikTok, Instagram Reels, and YouTube Shorts that captivate your audience.',
        icon: '📱',
    },
    {
        title: 'Educational Videos',
        description: 'Transform historical figures, authors, or scientists into talking presenters for engaging educational content.',
        icon: '🎓',
    },
    {
        title: 'Memorial & Heritage',
        description: 'Bring old family photos to life. Create meaningful tributes and preserve memories with talking heritage photos.',
        icon: '🕊️',
    },
    {
        title: 'Marketing & Advertising',
        description: 'Create dynamic product spokesperson videos from a single photo. Perfect for ads, landing pages, and campaigns.',
        icon: '📢',
    },
    {
        title: 'Entertainment & Fun',
        description: 'Make your pets talk, animate memes, or create hilarious talking photos to share with friends and family.',
        icon: '🎉',
    },
    {
        title: 'Personalized Messages',
        description: 'Send unique talking photo greetings for birthdays, holidays, and special occasions that recipients will never forget.',
        icon: '💌',
    },
];
const faqs = [
    {
        question: 'How do I make an image talk with AI?',
        answer: 'Upload any portrait photo to our platform, write a script or type the text you want the image to say, choose an AI voice and language, then click generate. Our AI will animate the face with realistic lip-sync, facial expressions, and natural head motion. The entire process takes just 2-5 minutes and requires no technical skills.',
    },
    {
        question: 'What types of images work best?',
        answer: 'Clear portrait photos with visible faces work best. The AI also works great with professional headshots, AI-generated portraits, digital illustrations, anime characters, and even historical photographs. For optimal results, use images where the face is front-facing or at a slight angle, with good lighting and resolution.',
    },
    {
        question: 'Can I make historical photos talk?',
        answer: 'Yes! Our AI can animate historical photographs, bringing figures from the past to life with remarkable realism. This feature is popular for educational content, museum exhibits, heritage projects, and bringing family history alive. Even old black-and-white photos can be animated effectively.',
    },
    {
        question: 'Is it possible to use AI-generated images?',
        answer: 'Absolutely! Images created with AI generators like DALL-E, Midjourney, Stable Diffusion, or our built-in AI Image Generator work perfectly with our talking photo technology. This means you can create a completely unique character and make it speak in minutes.',
    },
    {
        question: 'How long can the talking photo videos be?',
        answer: 'Videos can be up to 2 minutes in length, which is ideal for most social media platforms, short presentations, and marketing clips. For longer content, you can create multiple clips and combine them. The processing time scales with video length, typically taking 2-5 minutes.',
    },
    {
        question: 'Can I use my own voice instead of AI voices?',
        answer: 'Yes, you can upload your own audio recording. The AI will sync the lip movements and facial expressions of the image to match your voice perfectly. This is great for personal messages, professional presentations, or when you want a specific voice that matches your brand.',
    },
];
const relatedTools = [
    {
        title: 'AI Talking Head Videos',
        description: 'Create professional talking head videos with AI-generated presenters for your content.',
        href: '/ai-video-generator/talking-head',
    },
    {
        title: 'AI Spokesperson Videos',
        description: 'Generate polished spokesperson videos for marketing, training, and corporate communications.',
        href: '/ai-video-generator/spokesperson',
    },
    {
        title: 'AI Talking Avatar',
        description: 'Build custom AI avatars that speak your message with natural expressions and movements.',
        href: '/ai-video-generator/talking-avatar',
    },
];
const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebApplication',
            name: 'Easy-Peasy.AI Make Image Talk',
            description: 'Make any image talk with AI. Upload a photo and watch it come alive with realistic lip-sync and facial expressions.',
            url: 'https://easy-peasy.ai/ai-video-generator/make-image-talk',
            applicationCategory: 'MultimediaApplication',
            operatingSystem: 'Web',
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
            },
            creator: {
                '@type': 'Organization',
                name: 'Easy-Peasy.AI',
                url: 'https://easy-peasy.ai',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: 'https://easy-peasy.ai',
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'AI Video Generator',
                    item: 'https://easy-peasy.ai/ai-video-generator',
                },
                {
                    '@type': 'ListItem',
                    position: 3,
                    name: 'Make Image Talk',
                    item: 'https://easy-peasy.ai/ai-video-generator/make-image-talk',
                },
            ],
        },
    ],
};
export default function MakeImageTalkLanding() {
    const { t } = useLocale('ai-video-make-image-talk');
    const [isMuted, setIsMuted] = useState(true);
    const afterVideoRef = useRef<HTMLVideoElement>(null);
    const toggleSound = () => {
        if (afterVideoRef.current) {
            afterVideoRef.current.muted = !afterVideoRef.current.muted;
            setIsMuted(afterVideoRef.current.muted);
        }
    };
    return translateReactTree(<>
      <Seo templateTitle={t('Make Image Talk with AI | Turn Any Photo into a Talking Video')} description={t('Make any image talk with AI. Upload a photo and watch it come alive with realistic lip-sync and facial expressions. Works with portraits, illustrations, paintings & AI-generated images.')} image='https://easy-peasy.ai/api/og-template?title=Make%20Image%20Talk%20with%20AI'/>

      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}/>
      </Head>

      <style jsx global>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-3000 {
          animation-delay: 3s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-5000 {
          animation-delay: 5s;
        }
      `}</style>

      <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950'>
        <LandingPageNavigation />

        {/* ===== HERO SECTION ===== */}
        <section className='relative overflow-hidden pb-20 pt-32 sm:pt-40'>
          {/* Background gradient orbs */}
          <div className='absolute inset-0 -z-10'>
            <div className='animate-blob absolute left-1/4 top-0 h-72 w-72 rounded-full bg-emerald-300 opacity-70 mix-blend-multiply blur-xl filter dark:opacity-20 sm:h-96 sm:w-96'/>
            <div className='animate-blob animation-delay-2000 absolute right-1/4 top-20 h-72 w-72 rounded-full bg-teal-300 opacity-70 mix-blend-multiply blur-xl filter dark:opacity-20 sm:h-96 sm:w-96'/>
            <div className='animate-blob animation-delay-4000 absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-300 opacity-70 mix-blend-multiply blur-xl filter dark:opacity-20 sm:h-96 sm:w-96'/>
          </div>

          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* Left — text */}
              <div className='text-center lg:text-left'>
                {/* Glass morphism badge */}
                <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-emerald-200/60 bg-white/60 px-5 py-2.5 shadow-lg backdrop-blur-md dark:border-emerald-500/30 dark:bg-gray-800/60'>
                  <SparklesIcon className='h-5 w-5 text-emerald-600 dark:text-emerald-400'/>
                  <span className='text-sm font-semibold text-emerald-700 dark:text-emerald-300'>
                    AI Photo Animation Technology
                  </span>
                </div>

                <h1 className='text-5xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl'>
                  <span className='text-gray-900 dark:text-white'>
                    Make Any Image
                  </span>
                  <br />
                  <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                    Talk With AI
                  </span>
                </h1>

                <p className='mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300 sm:text-xl'>
                  Transform any static photo into a dynamic talking video.
                  Upload a portrait, write a script, and watch your image come
                  alive with realistic lip-sync, facial expressions, and natural
                  head motion.
                </p>

                <div className='mt-10 flex flex-col items-center gap-4 sm:flex-row lg:justify-start'>
                  <a href='/ai-talking-videos' className='inline-block rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-4 text-lg font-semibold text-white shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-[1.03] dark:shadow-emerald-500/15'>
                    Make an Image Talk - Free
                  </a>
                  <a href='#before-after' className='group inline-flex items-center gap-2 text-base font-medium text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400'>
                    See Before &amp; After
                    <svg className='h-4 w-4 transition-transform group-hover:translate-y-1' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 14l-7 7m0 0l-7-7m7 7V3'/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Right — two videos side by side */}
              <div className='flex justify-center gap-4 lg:justify-end'>
                {[
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b4cc830f-c5ca-435e-8da0-05a0049898aa.mp4',
                label: 'Male Presenter',
            },
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b5ef5f5c-cd31-42ef-864b-ddbc3a867a84.mp4',
                label: 'Female Presenter',
            },
        ].map((video) => (<div key={video.label} className='relative w-full max-w-[260px] overflow-hidden rounded-2xl border border-emerald-200/40 shadow-2xl shadow-emerald-500/10 dark:border-emerald-800/30'>
                    <div className='relative aspect-[9/16] bg-gray-100 dark:bg-gray-800'>
                      <video autoPlay muted loop playsInline className='h-full w-full object-cover' src={video.src}/>
                    </div>
                    <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-3 pt-8'>
                      <span className='text-xs font-medium text-white/90'>
                        {video.label}
                      </span>
                    </div>
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== LOGOS CLOUD ===== */}
        <LogosCloud />

        {/* ===== BEFORE & AFTER SECTION ===== */}
        <section id='before-after' className='py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                From Still Photo to{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  Talking Video
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                See the transformation our AI delivers in just minutes
              </p>
            </div>

            <div className='mt-16 grid items-center gap-6 md:grid-cols-[1fr_auto_1fr]'>
              {/* Before card */}
              <div className='group rounded-2xl border border-gray-200 bg-white/80 p-8 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/80'>
                <div className='mb-4 inline-flex rounded-full bg-gray-100 px-4 py-1.5 text-sm font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300'>
                  Before
                </div>
                <div className='overflow-hidden rounded-xl'>
                  <img src='https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a3e9bf3d-fead-4781-857c-f4d51807f7c3.png' alt='Static portrait photo before AI animation' className='aspect-[3/4] w-full object-cover'/>
                </div>
                <p className='mt-4 text-center text-sm text-gray-500 dark:text-gray-400'>
                  A regular portrait photo - still, silent, and static
                </p>
              </div>

              {/* Arrow indicator */}
              <div className='flex items-center justify-center'>
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30'>
                  <svg className='h-8 w-8 text-white max-md:rotate-90' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                  </svg>
                </div>
              </div>

              {/* After card */}
              <div className='group rounded-2xl border border-emerald-200 bg-white/80 p-8 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl dark:border-emerald-500/30 dark:bg-gray-800/80'>
                <div className='mb-4 inline-flex rounded-full bg-emerald-100 px-4 py-1.5 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'>
                  After
                </div>
                <div className='relative overflow-hidden rounded-xl'>
                  <video ref={afterVideoRef} autoPlay muted loop playsInline className='aspect-[3/4] w-full object-cover' src='https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_ecec6ba9-7d55-40bc-9917-8bdf666dec1b.mp4'/>
                  <button onClick={toggleSound} className='absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition hover:bg-black/70' aria-label={isMuted ? 'Unmute' : 'Mute'}>
                    {isMuted ? (<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'/>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2'/>
                      </svg>) : (<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'/>
                      </svg>)}
                  </button>
                </div>
                <p className='mt-4 text-center text-sm text-gray-500 dark:text-gray-400'>
                  A lifelike talking video with lip-sync and expressions
                </p>
              </div>
            </div>

            <p className='mx-auto mt-12 max-w-3xl text-center text-base text-gray-600 dark:text-gray-300'>
              Upload any portrait photo and our AI brings it to life with
              realistic lip movements, facial expressions, and natural head
              motion. The result is a compelling video that looks and feels
              natural.
            </p>
          </div>
        </section>

        {/* ===== WHAT TYPES OF IMAGES WORK ===== */}
        <section className='bg-white/50 py-20 dark:bg-gray-900/50 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                What Types of Images{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  Work Best?
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                Our AI works with a wide variety of image types and styles
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {imageTypes.map((type, index) => (<div key={index} className='group rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-md backdrop-blur-sm transition-all duration-300 hover:border-emerald-300 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/80 dark:hover:border-emerald-500/50' style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
            }}>
                  <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-50 text-3xl transition-transform duration-300 group-hover:scale-110 dark:bg-emerald-500/10'>
                    {type.icon}
                  </div>
                  <h3 className='mb-2 text-lg font-bold text-gray-900 dark:text-white'>
                    {type.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-gray-600 dark:text-gray-300'>
                    {type.description}
                  </p>
                </div>))}
            </div>

            <div className='mt-10 text-center'>
              <p className='inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/80 px-6 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'>
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'/>
                </svg>
                Best results with images where the face is clearly visible and
                facing forward
              </p>
            </div>
          </div>
        </section>

        {/* ===== FEATURES SECTION ===== */}
        <section className='py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Everything You Need to{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  Make Images Talk
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                Powerful features that make creating talking photo videos simple
                and professional
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {features.map((feature, index) => (<div key={index} className='group rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-md backdrop-blur-sm transition-all duration-300 hover:border-emerald-300 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/80 dark:hover:border-emerald-500/50' style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.08}s both`,
            }}>
                  <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-50 text-3xl transition-transform duration-300 group-hover:scale-110 dark:bg-emerald-500/10'>
                    {feature.icon}
                  </div>
                  <h3 className='mb-2 text-lg font-bold text-gray-900 dark:text-white'>
                    {feature.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-gray-600 dark:text-gray-300'>
                    {feature.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===== STEP-BY-STEP TUTORIAL ===== */}
        <section className='bg-white/50 py-20 dark:bg-gray-900/50 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                How to Make an Image{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  Talk with AI
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                Four simple steps to turn any photo into a talking video
              </p>
            </div>

            <div className='mt-16 grid gap-8 lg:grid-cols-2'>
              {steps.map((step, index) => (<div key={index} className='group relative rounded-2xl border border-gray-200 bg-white/80 p-8 shadow-md backdrop-blur-sm transition-all duration-300 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/80' style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`,
            }}>
                  <div className='absolute -left-3 -top-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-sm font-bold text-white shadow-lg shadow-emerald-500/25'>
                    {step.number}
                  </div>
                  <div className='ml-6'>
                    <h3 className='mb-3 text-xl font-bold text-gray-900 dark:text-white'>
                      {step.title}
                    </h3>
                    <p className='leading-relaxed text-gray-600 dark:text-gray-300'>
                      {step.description}
                    </p>
                  </div>
                </div>))}
            </div>

            <div className='mt-12 text-center'>
              <a href='/ai-talking-videos' className='inline-block rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-4 text-lg font-semibold text-white shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-[1.03]'>
                Start Making Images Talk
              </a>
            </div>
          </div>
        </section>

        {/* ===== USE CASES SECTION ===== */}
        <section className='py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Popular{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  Use Cases
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                Discover how creators, businesses, and educators use talking
                photo AI
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {useCases.map((useCase, index) => (<div key={index} className='group rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-md backdrop-blur-sm transition-all duration-300 hover:border-emerald-300 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/80 dark:hover:border-emerald-500/50' style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
            }}>
                  <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-50 text-3xl transition-transform duration-300 group-hover:scale-110 dark:bg-emerald-500/10'>
                    {useCase.icon}
                  </div>
                  <h3 className='mb-2 text-lg font-bold text-gray-900 dark:text-white'>
                    {useCase.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-gray-600 dark:text-gray-300'>
                    {useCase.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===== FAQ SECTION ===== */}
        <section className='bg-white/50 py-20 dark:bg-gray-900/50 sm:py-28'>
          <div className='mx-auto max-w-3xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Frequently Asked{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  Questions
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                Everything you need to know about making images talk with AI
              </p>
            </div>

            <div className='mt-12 space-y-4'>
              {faqs.map((faq, index) => (<details key={index} className='group rounded-2xl border border-gray-200 bg-white/80 shadow-md backdrop-blur-sm transition-all duration-300 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800/80 [&[open]]:shadow-lg'>
                  <summary className='flex cursor-pointer items-center justify-between px-6 py-5 text-left text-lg font-semibold text-gray-900 dark:text-white'>
                    {faq.question}
                    <svg className='ml-4 h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 group-open:rotate-180 dark:text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7'/>
                    </svg>
                  </summary>
                  <div className='px-6 pb-5'>
                    <p className='leading-relaxed text-gray-600 dark:text-gray-300'>
                      {faq.answer}
                    </p>
                  </div>
                </details>))}
            </div>
          </div>
        </section>

        {/* ===== PRICING CTA SECTION ===== */}
        <section className='py-20 sm:py-28'>
          <div className='mx-auto max-w-4xl px-4 sm:px-6 lg:px-8'>
            <div className='overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-600 p-10 shadow-2xl shadow-emerald-500/25 sm:p-16'>
              <div className='text-center'>
                <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl'>
                  Ready to Make Your Images Talk?
                </h2>
                <p className='mx-auto mt-4 max-w-2xl text-lg text-emerald-100'>
                  Join thousands of creators who are already bringing their
                  photos to life with AI
                </p>

                <ul className='mx-auto mt-8 grid max-w-lg gap-3 text-left sm:grid-cols-2'>
                  {[
            'Works with any portrait',
            '40+ languages supported',
            'HD 1080p video output',
            'Commercial usage rights',
            'No credit card required',
            'Results in minutes',
        ].map((item, index) => (<li key={index} className='flex items-center gap-2 text-sm text-white'>
                      <CheckIcon className='h-5 w-5 flex-shrink-0 text-emerald-200'/>
                      {item}
                    </li>))}
                </ul>

                <div className='mt-10'>
                  <a href='/ai-talking-videos' className='inline-block rounded-full bg-white px-8 py-4 text-lg font-semibold text-emerald-700 shadow-lg transition-all duration-300 hover:bg-emerald-50 hover:shadow-xl hover:scale-[1.03]'>
                    Get Started Free
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== RELATED AI VIDEO TOOLS ===== */}
        <section className='bg-white/50 py-20 dark:bg-gray-900/50 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
                Related{' '}
                <span className='bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-emerald-400 dark:to-teal-400'>
                  AI Video Tools
                </span>
              </h2>
              <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
                Explore more AI-powered video creation tools
              </p>
            </div>

            <div className='mt-12 grid gap-6 sm:grid-cols-3'>
              {relatedTools.map((tool, index) => (<Link key={index} href={tool.href} className='group block rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-md backdrop-blur-sm transition-all duration-300 hover:border-emerald-300 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800/80 dark:hover:border-emerald-500/50'>
                  <h3 className='mb-2 text-lg font-bold text-gray-900 transition-colors group-hover:text-emerald-600 dark:text-white dark:group-hover:text-emerald-400'>
                    {tool.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-gray-600 dark:text-gray-300'>
                    {tool.description}
                  </p>
                  <div className='mt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400'>
                    Learn more
                    <svg className='h-4 w-4 transition-transform group-hover:translate-x-1' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7'/>
                    </svg>
                  </div>
                </Link>))}
            </div>
          </div>
        </section>

        {/* ===== SHARED COMPONENTS ===== */}
        <FeaturedLogos />
        <Testimonials />
        <CallToAction preset='ai-talking-videos'/>
        <Footer />
      </div>
    </>, t);
}
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
    };
};

    async function __Next_Translate__getStaticProps__1a0cbfb65cc__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/make-image-talk',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb65cc__ as getStaticProps }
  